import capitalize from 'lodash/capitalize';
import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import { PlatformVoice } from 'lib/platform/types';
import { VoiceInfo } from 'modules/sdk/lib';
import { getIDQualified } from 'modules/settings-sync/helpers';
import { Nullable } from 'utils/types';

import { VoiceLabelType } from '../types';

export type VoicesByLanguage = Record<string, VoiceInfo[]>;

export const SPEECH_SYNTH_ENGINE = 'speechSynth';
export const LOCAL_ENGINE = 'local';

export const getLegacyIdQualifiedFromPlatformVoice = (voice: PlatformVoice): string => {
  return `${voice.provider?.toLowerCase()}-${voice.slug.toLowerCase()}-${voice.locale.toLowerCase()}`;
};

export const convertOldToNewStyleID = (oldStyleId: string, voices: VoiceInfo[]): string => {
  const voice = getVoiceByIDQualified(oldStyleId, voices);
  return voice?.slug || oldStyleId; // Fallback to old ID if no new style ID exists
};

export const groupVoicesByLanguage = (voices: VoiceInfo[]): VoicesByLanguage => {
  const uniqVoices = uniqBy(voices, v => v.id);
  const groups = groupBy(uniqVoices, v => v.language);
  return groups;
};

export const getUniqueLanguageCodes = (voicesByLanguage: VoicesByLanguage): string[] => {
  const allVoices = Object.values(voicesByLanguage).flat();
  return uniq(allVoices.map(voice => voice.languageCodeWithLocale));
};

export const getVoiceByIDQualified = (idQualified: string, voices: VoiceInfo[]) => {
  return find(voices, v => {
    const voiceId = getIDQualified(v);
    const voiceSlug = v.slug;
    return voiceId === idQualified || voiceSlug === idQualified;
  });
};

export type LanguageMap = { id: string; name: string; regions: string[] };

function getLocalizedLanguageName(code: string, locale: string): string {
  try {
    const languageName = new Intl.DisplayNames([locale], { type: 'language' }).of(code);
    return languageName ? capitalize(languageName) : code;
  } catch {
    return code;
  }
}

export const transformLanguageCodes = (codes: string[]): LanguageMap[] => {
  const grouped = groupBy(codes, code => code.split('-')[0]);

  return map(grouped, (values, key) => {
    const regions = values.map(code => code.split('-')[1]).filter(Boolean);

    return {
      id: key,
      name: getLocalizedLanguageName(key, key), // Get the name in the local language
      regions
    };
  });
};

export function getRecommendedTypeFromLabel(labels: Nullable<string[]>, isPremiumUser?: boolean): VoiceLabelType | undefined {
  const label = (find(labels, l => l != null) || 'premium') as VoiceLabelType;
  if (isPremiumUser && label === 'premium') {
    // hide premium label for Premium users
    return;
  }
  return label;
}

export function getListTypeFromLabel(labels: Nullable<string[]>, isPremiumUser?: boolean): VoiceLabelType | undefined {
  const label = (find(labels, l => l != null) || 'premium') as VoiceLabelType;
  if (isPremiumUser && (label === 'premium' || label.startsWith('label:'))) {
    // hide premium and vms label in all tab
    return;
  }
  if (!isPremiumUser && label.startsWith('label:')) {
    // show vms label for non-premium users as premium in all tab
    return 'premium';
  }
  return label;
}

export function convertTypetoText(type: VoiceLabelType) {
  switch (type) {
    case 'ai-enhanced':
      return 'AI Enhanced';

    case 'celebrity':
      return 'Celebrity';

    case 'custom':
      return 'Custom';

    case 'premium':
      return 'Premium';

    case 'label:partner':
      return 'label-partner';

    case 'label:founder':
      return 'label-founder';

    case 'label:narrator':
      return 'label-narrator';

    default:
      return 'Premium';
  }
}
